import * as dto from '@epicuro-next/data/baskets-dto';
import { MaterialWidthIds } from '@epicuro-next/models/products';
import {
  isNonNullish,
  isNullish,
  mapProductId,
  utcDateTime,
} from '@epicuro-next/utilities';
import {
  Basket,
  BasketItem,
  BasketItemProductType,
  BasketQuantity,
  BasketRow,
  BasketRowPrice,
  BasketRowProductType,
  BasketRowsGroup,
  BasketStatus,
  DeliveryWindowType,
  Maybe,
  OrderType,
  ShippingOption,
  Total,
  TotalColorItem,
  TotalDiscounts,
  TotalItem,
  TotalProductItem,
} from 'graphql-api/baskets-api-interfaces';
import { pipe } from 'rxjs';

import { makeBasketLineColumns, mapColumns } from '../shared';
import { getDiscountsType } from '../shared/get-discounts-type';

import { findBasketAccount } from './basket-accounts.mapping';
import {
  makePreviewItems,
  makeProductLineColumns,
  makeThumbnails,
} from './basket-columns.mapping';
import {
  BasketMappingSettings,
  GetRowsFn,
  GroupingFn,
  Mappings,
} from './basket.models';
import { getImage } from './images.mapping';
import { getPricesForItem, getPricesForPrepackItem } from './prices.mapping';
import {
  findProduct,
  findProductByType,
  getArticle,
  getPrepackQuantities,
  getPrepackSize,
  getProductInfo,
  getProductKey,
  getProductType,
  groupItemsByMaterial,
  toPrepack,
} from './products.mapping';

// tslint:disable-next-line:parameters-max-number
export function mapBasket(
  basket: dto.ExtendedDtoBasket,
  status: BasketStatus,
  basketItems: BasketItem[],
  groups: BasketRowsGroup[],
  total: Total,
  bulkTotal: Total,
  realTotal: Total,
  materialsQuantities: BasketQuantity[],
  shippingInfo: {
    shippingOptions: ShippingOption[];
    selectedShippingOptionId?: string;
  },
  alternativeAddress: Maybe<dto.AlternativeAddress>,
): Basket {
  return {
    basketId: basket.basketId,
    etag: basket.etag,
    status,
    basketItems,
    alternativeAddress,
    shippingOptions: shippingInfo.shippingOptions,
    selectedShippingOptionId: shippingInfo.selectedShippingOptionId,
    allowUserToSpecifyShipOrderInFullForNowOrder:
      basket.allowUserToSpecifyShipOrderInFullForNowOrder,
    userSpecifiedGroupedDelivery: basket.userSpecifiedGroupedDelivery,
    groups,
    total,
    bulkTotal,
    realTotal,
    materialsQuantities,
    userId: basket.userId,
    userDisplayName: basket.userDisplayName,
    orderReferenceNumber: basket.orderReferenceNumber,
    clientReferenceNumber: basket.clientReferenceNumber,
    checkOrderExpiryDate: basket.checkOrderExpiryUtc,
    submittedTime: basket.submittedTime,
    assignee: basket.assignee,
    assigner: basket.assigner,
    reviewNeeded: basket.reviewNeeded,
    reviewStatus: basket.reviewStatus,

    // current assumption is that we can translate product account data to the basket level
    // because its defined on the account level
    // so we can take it from any product
    deliveryWindowType: pipe(
      defineDeliveryWindowType,
      mapDeliveryWindowType,
    )(basket),
  };
}

function defineDeliveryWindowType(
  basket: dto.ExtendedDtoBasket,
): dto.DeliveryWindowType {
  if (
    !basket.referencedProducts ||
    !basket.referencedProducts.length ||
    !basket.referencedProducts[0]?.accountData?.deliveryWindowType
  ) {
    return dto.DeliveryWindowType.day;
  }
  return basket.referencedProducts[0].accountData.deliveryWindowType;
}

function mapDeliveryWindowType(
  deliveryWindowType: dto.DeliveryWindowType,
): DeliveryWindowType {
  return deliveryWindowTypeMap[deliveryWindowType];
}

const deliveryWindowTypeMap: { [type: number]: DeliveryWindowType } = {
  [dto.DeliveryWindowType.month]: DeliveryWindowType.Month,
  [dto.DeliveryWindowType.week]: DeliveryWindowType.Week,
  [dto.DeliveryWindowType.day]: DeliveryWindowType.Day,
};

/**
 * All items belong one group.
 */
export function getBasketRows(items: dto.Item[], maps: Mappings) {
  const materialItemsMap = groupItemsByMaterial(items, maps.products);
  const prepacksRows = getBasketRowFromPrepacks(items, maps);
  const sizesRows = Object.keys(materialItemsMap).flatMap((materialKey) => {
    const materialItems = materialItemsMap[materialKey];
    return getBasketRowFromSizes(materialKey, materialItems, maps);
  });

  return sizesRows.concat(prepacksRows);
}

export function getDeepBasketRows(
  groups?: BasketRowsGroup[],
  rows?: BasketRow[],
): BasketRow[] {
  if (!groups?.length) {
    return rows || [];
  }

  const nestedGroups = groups
    .flatMap((group) => group.groups)
    ?.filter(isNonNullish);
  const nestedRows = groups
    .flatMap((group) => group.rows)
    ?.filter(isNonNullish);

  return getDeepBasketRows(nestedGroups, (rows || []).concat(nestedRows));
}

/**
 * Creates map of item ids to items.
 */
export function getItemsMap(items: dto.Item[]): dto.ItemsMap {
  const itemsMap: { [id: string]: dto.Item } = {};
  for (const item of items) {
    itemsMap[item.itemId] = item;
  }

  return itemsMap;
}

export function mapOrderType(orderType: number) {
  return orderTypeMap[orderType];
}

const orderTypeMap: { [orderType: number]: OrderType } = {
  0: OrderType.None,
  1: OrderType.Now,
  2: OrderType.Future,
  3: OrderType.Mixed,
};

/**
 * Maps dbo.Item to BasketItem
 */

export function mapBasketItems(
  items: dto.Item[],
  products: dto.ProductsMap,
  basketAccounts: dto.BasketAccountMap,
  settings: Partial<BasketMappingSettings>,
) {
  return items.map((item) => {
    const product = findProduct(item, products);
    const prepack = toPrepack(product);
    const basketAccount = findBasketAccount(item, basketAccounts);

    const productInfo = getProductInfo(product, products);

    return prepack === undefined
      ? mapBasketItem(
          item,
          BasketItemProductType.Size,
          basketAccount,
          productInfo,
          product.title,
          settings,
        )
      : mapBasketItem(
          item,
          BasketItemProductType.Prepack,
          basketAccount,
          productInfo,
          prepack?.title,
          settings,
          getPrepackSize(prepack),
        );
  });
}

function mapBasketItem(
  item: dto.Item,
  productType: BasketItemProductType,
  basketAccount: dto.BasketAccount,
  productData: dto.IProductInfo,
  title: string,
  settings: Partial<BasketMappingSettings>,
  prepackSize?: number,
): BasketItem {
  const { consignmentStartDate, consignmentEndDate, requestedDeliveryDate } =
    getDeliveryInfo(item, basketAccount);

  return {
    basketItemId: item.itemId,
    basketItemOrigin: item.basketItemOrigin,
    accountId: basketAccount.account.accountId,
    addressId: basketAccount.address?.addressId ?? basketAccount.addressId,
    productId: item.productId,
    displayedProductId: mapProductId(item.productId, settings.additionalPrefix),
    brandId: item.brandId,
    articleId: productData.articleId,
    materialId: productData.materialId,
    prepackMaterialIds: productData.prepackMaterialIds,
    orderType: mapOrderType(item.orderType),
    prepackSize,
    productType,
    note: item.note,
    requestedQuantity: item.requestedQuantity,
    confirmedQuantity: item.confirmedQuantity,
    requestedDeliveryDate: requestedDeliveryDate ?? item.requestedDeliveryDate,
    created: item.createdDateTimeUtc,
    lastUpdated: item.lastUpdatedDateTimeUtc,
    displayDateFrom: getDate(item.displayDateFrom),
    displayDateTo: getDate(item.displayDateTo),
    expectedDeliveryDate: getDate(item.expectedDeliveryDate),
    title: mapProductId(title, settings.additionalPrefix),
    confirmedOrderNumber: item.confirmedOrderNumber,
    consignmentStartDate:
      consignmentStartDate ??
      item.consignmentStartDate ??
      requestedDeliveryDate ??
      item.requestedDeliveryDate,
    consignmentEndDate,
    inventoryId: item.inventoryId,
  };
}

export function getLastUpdatedDate(lastUpdatedAcc: string[]) {
  return lastUpdatedAcc.reduce((latestDate, currentDate) => {
    return latestDate < currentDate ? currentDate : latestDate;
  });
}

export function getCreatedDate(createdAcc: string[]) {
  return createdAcc.reduce((minDate, currentDate) => {
    return minDate > currentDate ? currentDate : minDate;
  });
}

/**
 * Creates groups, rows and total.
 */
export function getNestedObjects(
  items: dto.Item[],
  maps: Mappings,
  groupFn?: GroupingFn,
  getRowsFn?: GetRowsFn,
) {
  let localMaps = { ...maps };

  if (localMaps.settings?.manualDiscounts) {
    localMaps = {
      ...maps,
      settings: {
        ...maps.settings,
        manualDiscounts: filterOutUnrelatedManualDiscounts(
          items,
          localMaps.settings.manualDiscounts,
        ),
      },
    };
  }

  const groups = groupFn ? groupFn(items, localMaps) : undefined;

  const rows =
    !groups?.length && getRowsFn ? getRowsFn(items, localMaps) : undefined;

  const nestedRows = getDeepBasketRows(groups, rows);

  const nestedObjects = [...(groups ?? []), ...(rows ?? [])];
  const totalsOfNestedObjects = nestedObjects.map((item) => item.total);
  const total = aggregateTotals(totalsOfNestedObjects);

  const bulkTotal = aggregateTotals(
    nestedObjects.map((item) => item.bulkTotal),
  );

  const discountedItemsorderType = getDiscountsType(items);

  const realTotal = aggregateTotals(
    nestedObjects.map((item) => item.realTotal),
  );

  const isManualDiscountApplied =
    Object.keys(localMaps.settings.manualDiscounts ?? {}).length > 0;

  const columnsData = makeBasketLineColumns({
    total,
    isManualDiscountApplied,
    orderType: discountedItemsorderType,
  });
  const columns = mapColumns(columnsData, localMaps);

  const thumbnails = makeThumbnails(nestedRows ?? [], localMaps);

  return {
    groups,
    rows,
    total,
    bulkTotal,
    realTotal,
    created: getCreatedDate(items.map((item) => item.createdDateTimeUtc)),
    lastUpdated: getLastUpdatedDate(
      items.map((item) => item.lastUpdatedDateTimeUtc),
    ),
    columns,
    thumbnails,
  };
}

function filterOutUnrelatedManualDiscounts(
  addressItems: dto.Item[],
  manualDiscounts: Required<Mappings['settings']>['manualDiscounts'],
): typeof manualDiscounts {
  const applicableBasketItemIds = addressItems.map((i) => i.itemId);

  const applicableManualDiscounts: ReturnType<
    typeof filterOutUnrelatedManualDiscounts
  > = {};

  Object.keys(manualDiscounts).forEach((basketItemId) => {
    if (applicableBasketItemIds.includes(basketItemId)) {
      applicableManualDiscounts[basketItemId] = manualDiscounts[basketItemId];
    }
  });

  return applicableManualDiscounts;
}

/**
 * Aggregate totals.
 */
export function aggregateTotals(totals: Total[]) {
  return totals.reduce(
    (result: Total, next: Total) => ({
      requested: aggregateTotalItem(result.requested, next.requested),
      confirmed: next.confirmed
        ? aggregateTotalItem(result.confirmed ?? emptyTotalItem, next.confirmed)
        : result.confirmed,
      simulated: next.simulated
        ? aggregateTotalItem(result.simulated ?? emptyTotalItem, next.simulated)
        : result.simulated,
      products: aggregateTotalProducts(
        result.products || [],
        next.products || [],
      ),
      discounts: aggregateTotalDiscounts(result.discounts, next.discounts),
      colors: aggregateTotalColors(result.colors || [], next.colors || []),
    }),
    {
      requested: {
        ...emptyTotalItem,
      },
      confirmed: undefined,
      simulated: undefined,
      products: [],
      discounts: { wsp: 0, rrp: 0 },
      colors: [],
    } as Total,
  );
}

const emptyTotalItem = {
  quantity: 0,
  rrp: 0,
  wsp: 0,
  wspd: 0,
  displayedwsp: 0,
  net: 0,
  currency: '',
  currencyRrp: '',
};

function aggregateTotalItem(result: TotalItem, next: TotalItem) {
  // BE is not available case
  const wspToAdd = next.wsp > 0 ? next.wsp : next.net;

  return {
    quantity: aggregateTotalQuantity(result.quantity, next.quantity),
    rrp: result.rrp + next.rrp,
    wsp: result.wsp + wspToAdd,
    wspd: result.wspd + next.wspd,
    displayedwsp: result.displayedwsp + next.displayedwsp,
    net: result.net + next.net,
    currency: next.currency ?? result?.currency ?? '',
    currencyRrp: next.currencyRrp ?? result?.currencyRrp ?? '',
  };
}

function aggregateTotalQuantity(result?: number | null, next?: number | null) {
  if (isNonNullish(result) && isNonNullish(next)) {
    return result + next;
  }

  return undefined;
}

function aggregateTotalProducts(
  result: TotalProductItem[],
  next: TotalProductItem[],
) {
  return [...result, ...next].reduce(
    (uqProducts: TotalProductItem[], product: TotalProductItem) => {
      const uniqueProductIndex = uqProducts.findIndex(
        (e) =>
          e.brandId === product.brandId && e.productId === product.productId,
      );
      if (uniqueProductIndex !== -1) {
        uqProducts[uniqueProductIndex] = aggregateTotalProduct(
          uqProducts[uniqueProductIndex],
          product,
        );
        return uqProducts;
      }

      return [...uqProducts, product];
    },
    [],
  );
}

function aggregateTotalProduct(
  result: TotalProductItem,
  next: TotalProductItem,
): TotalProductItem {
  const basketItemIds = new Set(
    result.basketItemIds.concat(next.basketItemIds),
  );
  return {
    brandId: result.brandId,
    productId: result.productId,
    title: result.title,
    imageUrl: result.imageUrl,
    basketItemIds: [...basketItemIds],
    quantity: result.quantity + next.quantity,
    isCloseOutProduct: result.isCloseOutProduct || next.isCloseOutProduct,
    gender: result.gender || next.gender,
  };
}

function aggregateTotalColors(
  result: TotalColorItem[],
  next: TotalColorItem[],
) {
  return [...result, ...next].reduce(
    (colors: TotalColorItem[], color: TotalColorItem) => {
      const uniqueColorIndex = colors.findIndex((e) => e.hex === color.hex);
      if (uniqueColorIndex !== -1) {
        colors[uniqueColorIndex] = aggregateTotalColor(
          colors[uniqueColorIndex],
          color,
        );
        return colors;
      }

      return [...colors, color];
    },
    [],
  );
}

function aggregateTotalDiscounts(
  result: TotalDiscounts | undefined | null,
  next: TotalDiscounts | undefined | null,
) {
  return {
    wsp: (result?.wsp ?? 0) + (next?.wsp ?? 0),
    rrp: (result?.rrp ?? 0) + (next?.rrp ?? 0),
  };
}

function aggregateTotalColor(result: TotalColorItem, next: TotalColorItem) {
  return {
    hex: result.hex,
    quantity: result.quantity + next.quantity,
  };
}

/**
 * Prepacks may have multiple materials, thus the row have to be based on whole prepack.
 */
function getBasketRowFromPrepacks(
  items: dto.Item[],
  maps: Mappings,
): BasketRow[] {
  const prepacksOnly = items.filter((item) => {
    const sizeOrPrepack = findProduct(item, maps.products);
    return toPrepack(sizeOrPrepack);
  });

  if (!prepacksOnly.length) {
    return [];
  }

  const itemsPerDelivery = prepacksOnly.reduce((deliveryItems, item) => {
    const key = getDeliveryKeyForPrepack(item);
    deliveryItems[key] = deliveryItems[key] || [];
    deliveryItems[key].push(item);
    return deliveryItems;
  }, {} as { [deliveryId: string]: dto.Item[] });

  return Object.values(itemsPerDelivery).map((deliveryItems) => {
    const [anyPrepack] = deliveryItems;
    const prepack = findProduct(anyPrepack, maps.products);
    const imageUrl = getImage(prepack, dto.ProductType.Prepack, maps.products);
    const article = getArticle(prepack, dto.ProductType.Prepack, maps.products);
    const basketAccount = findBasketAccount(anyPrepack, maps.basketAccounts);

    const delivery = basketAccount.deliveries.find(
      ({ deliveryId }) => deliveryId === anyPrepack.deliveryId,
    ) as dto.Delivery;
    const product = {
      ...extractProductDataForBasketRow(prepack, maps.products, maps.settings),
      articleId: article.productId,
      displayedArticleId: mapProductId(
        article.productId,
        maps.settings?.additionalPrefix,
      ),
      productType: BasketRowProductType.Prepack,
      articleTitle: article.title,
      isclosedoutproduct: undefined,
      gender: undefined,
      prepack: getPrepackQuantities(prepack),
    };
    const prepackSize = getPrepackSize(prepack);
    const priceInfo = getPricesForPrepackItem(anyPrepack, prepackSize);

    const productInfo = getProductInfo(prepack, maps.products);

    const totalRequestedQuantity = deliveryItems.reduce(
      (sum, item) => sum + item.requestedQuantity,
      0,
    );
    const totalConfirmedQuantity = deliveryItems.reduce(
      (sum, item) => sum + (item.confirmedQuantity ?? 0),
      0,
    );

    const emptyTotal = aggregateTotals([]);
    const isBulk = delivery.isConsignmentOrder;

    const basketItems = [
      ...deliveryItems.map((item) =>
        mapBasketItem(
          item,
          BasketItemProductType.Prepack,
          basketAccount,
          productInfo,
          prepack.title,
          maps.settings,
          prepackSize,
        ),
      ),
    ];

    const [anyBasketItem] = basketItems;

    const manualDiscount =
      maps.settings.manualDiscounts?.[anyBasketItem.basketItemId]?.currentValue;

    const prepackTotals = getSizesTotal(deliveryItems, manualDiscount);
    const priceInfoTotal = aggregateTotals(prepackTotals);

    const total: Total = {
      requested: {
        ...priceInfoTotal.requested,
        ...getPrepackTotalItem(prepackSize, totalRequestedQuantity),
      } as TotalItem,
      ...(isNonNullish(totalConfirmedQuantity)
        ? {
            confirmed: {
              ...priceInfoTotal.confirmed,
              ...getPrepackTotalItem(prepackSize, totalConfirmedQuantity),
            } as TotalItem,
          }
        : null),
      products: getTotalProductItem(
        prepack,
        imageUrl,
        totalRequestedQuantity * prepackSize,
        deliveryItems.map((e) => e.itemId),
      ),
      colors: getPrepackTotalColorItem(prepack, maps.products),
      simulated: priceInfoTotal.simulated,
    };

    const isManualDiscountApplied = manualDiscount !== undefined;

    const columnsData = makeProductLineColumns(
      basketItems,
      delivery,
      priceInfo,
      total,
      isManualDiscountApplied,
    );
    const columns = mapColumns(columnsData, maps);

    const previewItems = makePreviewItems(basketItems, maps);

    return {
      imageUrl,
      delivery,
      product,
      priceInfo,
      total,
      created: anyPrepack.createdDateTimeUtc,
      lastUpdated: anyPrepack.lastUpdatedDateTimeUtc,
      bulkTotal: isBulk ? total : emptyTotal,
      realTotal: !isBulk ? total : emptyTotal,
      basketItems,
      columns,
      previewItems,
    };
  });
}

export function getSizesTotal(items: dto.Item[], manualDiscount?: number) {
  return items.map((size) => {
    const price = getPricesForItem(size);
    const requested = getTotalItem(price, size.requestedQuantity);
    const confirmed = getTotalItem(price, size.confirmedQuantity);
    const simulated = getTotalItem(
      price,
      size.confirmedQuantity,
      manualDiscount,
    );
    return {
      requested,
      ...(isNonNullish(size.confirmedQuantity) ? { confirmed } : {}),
      simulated,
      discounts: getDiscounts(size),
    };
  });
}

function getDiscounts(size: dto.Item) {
  let wsp = 0;
  let rrp = 0;
  if (size.prices.grossPrice?.unitPrice) {
    const diff =
      size.prices.grossPrice.unitPrice - (size.prices.netPrice?.unitPrice ?? 0);

    wsp = (diff < 0 ? 0 : diff) * (size.confirmedQuantity ?? 0);
  }
  if (size.prices.retailPrice?.unitPrice) {
    const diff =
      size.prices.retailPrice.unitPrice -
      (size.prices.netPrice?.unitPrice ?? 0);

    rrp = (diff < 0 ? 0 : diff) * (size.confirmedQuantity ?? 0);
  }
  return {
    wsp,
    rrp,
  };
}

/**
 * All items belong to one material.
 */
function getBasketRowFromSizes(
  materialKey: string,
  items: dto.Item[],
  maps: Mappings,
): BasketRow[] {
  const sizesOnly = items.filter((item) => {
    const sizeOrPrepack = findProduct(item, maps.products);
    return !toPrepack(sizeOrPrepack);
  });

  if (!sizesOnly.length) {
    return [];
  }
  const material = maps.products[materialKey];
  const imageUrl = getImage(material, dto.ProductType.Material, maps.products);
  const article = getArticle(material, dto.ProductType.Material, maps.products);
  const product = {
    ...extractProductDataForBasketRow(material, maps.products, maps.settings),
    productType: BasketRowProductType.Material,
    articleId: material.masterProductId,
    displayedArticleId: mapProductId(
      material.masterProductId,
      maps.settings?.additionalPrefix,
    ),
    articleTitle: article.title,
    isclosedoutproduct:
      material?.attributes?.isclosedoutproduct?.[0]?.toLocaleLowerCase() ===
      'true',
    gender: material?.attributes?.gender?.[0],
  };

  const itemsPerDelivery = sizesOnly.reduce((deliveryItems, item) => {
    const key = getDeliveryKey(item);
    deliveryItems[key] = deliveryItems[key] || [];
    deliveryItems[key].push(item);
    return deliveryItems;
  }, {} as { [deliveryId: string]: dto.Item[] });

  return Object.values(itemsPerDelivery).map((deliveryItems) => {
    const [anySize] = deliveryItems;
    const priceInfo = getPricesForItem(anySize);
    const totalQuantity = deliveryItems.reduce(
      (sum, item) => sum + (item.confirmedQuantity ?? item.requestedQuantity),
      0,
    );

    const basketItems = mapBasketItems(
      deliveryItems,
      maps.products,
      maps.basketAccounts,
      maps.settings,
    );

    const [anyBasketItem] = basketItems;

    const manualDiscount =
      maps.settings.manualDiscounts?.[anyBasketItem.basketItemId]?.currentValue;

    const delivery = Object.values(maps.basketAccounts)
      .reduce(
        (acc, { deliveries }) => [...acc, ...deliveries],
        [] as dto.Delivery[],
      )
      .find(
        ({ deliveryId }) => deliveryId === deliveryItems[0].deliveryId,
      ) as dto.Delivery;

    const sizeTotals = getSizesTotal(deliveryItems, manualDiscount);
    const priceTotal = aggregateTotals(sizeTotals);

    const total = {
      requested: priceTotal.requested,
      confirmed: priceTotal.confirmed,
      simulated: priceTotal.simulated,
      products: getTotalProductItem(
        material,
        imageUrl,
        totalQuantity,
        basketItems.map((e) => e.basketItemId),
      ),
      discounts: priceTotal?.discounts ?? { wsp: 0, rrp: 0 },
      colors: getTotalColorItem(material, totalQuantity),
    };

    const emptyTotal = aggregateTotals([]);
    const isBulk = delivery.isConsignmentOrder;

    const isManualDiscountApplied = manualDiscount !== undefined;

    const columnsData = makeProductLineColumns(
      basketItems,
      delivery,
      priceInfo,
      total,
      isManualDiscountApplied,
    );
    const columns = mapColumns(columnsData, maps);

    const previewItems = makePreviewItems(basketItems, maps);

    const materialWidthIds = maps.settings.showMaterialWidthIds
      ? getMaterialWidthIds(deliveryItems, maps.products)
      : [];

    return {
      imageUrl,
      product,
      priceInfo,
      basketItems,
      delivery,
      total,
      created: getCreatedDate(
        basketItems.map((item) => item.created as string),
      ),
      lastUpdated: getLastUpdatedDate(
        basketItems.map((item) => item.lastUpdated as string),
      ),
      bulkTotal: isBulk ? total : emptyTotal,
      realTotal: !isBulk ? total : emptyTotal,
      columns,
      previewItems,
      materialWidthIds,
    };
  });
}

function getDate(value: string | undefined | null) {
  return value ? value : undefined;
}

function getTotalItem(
  priceInfo: BasketRowPrice,
  quantity?: number,
  discount?: number,
): TotalItem {
  return {
    quantity,
    wsp: applyDiscount(getPriceForQuantity(priceInfo.wsp, quantity), discount),
    wspd: applyDiscount(
      getPriceForQuantity(priceInfo.wspd, quantity),
      discount,
    ),
    displayedwsp: applyDiscount(
      getPriceForQuantity(priceInfo.wspd ?? priceInfo.wsp, quantity),
      discount,
    ),
    rrp: applyDiscount(getPriceForQuantity(priceInfo.rrp, quantity), discount),
    net: applyDiscount(
      getPriceForQuantity(
        discount !== undefined ? priceInfo.wsp : priceInfo.net,
        quantity,
      ),
      discount,
    ),
    currency: priceInfo.currency ?? '',
    currencyRrp: priceInfo.currencyRrp ?? '',
  };
}

function getPrepackTotalItem(prepackSize: number, quantity?: number) {
  return {
    quantity: isNullish(quantity) ? undefined : quantity * prepackSize,
  };
}

function applyDiscount(price: number, discount?: number) {
  if (discount === undefined) {
    return price;
  }

  return price - price * discount;
}

function getPriceForQuantity(
  price: number | undefined | null,
  quantity?: number,
) {
  return isNullish(price) || isNullish(quantity) ? 0 : price * quantity;
}

function getTotalProductItem(
  product: dto.ReferencedProduct,
  imageUrl: string,
  quantity: number,
  basketItemIds: string[],
): TotalProductItem[] {
  return [
    {
      brandId: product.brand.brandId,
      productId: product.productId,
      title: product.title,
      basketItemIds,
      imageUrl,
      quantity,
      isCloseOutProduct:
        product?.attributes?.isclosedoutproduct?.[0]?.toLocaleLowerCase() ===
        'true',
      gender: product?.attributes?.gender?.[0],
    },
  ];
}

function getTotalColorItem(product: dto.ReferencedProduct, quantity: number) {
  const hexAttributeKeys = ['hexcode'];
  const hextAttribute = getProductAttribute(product, hexAttributeKeys);
  return hextAttribute
    ? [
        {
          hex: hextAttribute,
          quantity,
        },
      ]
    : [];
}

function getPrepackTotalColorItem(
  product: dto.ReferencedProduct,
  products: dto.ProductsMap,
) {
  const prepackColors = product.productCompositionPartReferences
    ?.map((part) =>
      getTotalColorItem(
        products[getProductKey(part.product.brandId, part.product.productId)],
        part.amount,
      ),
    )
    .filter((e) => e.length > 0)
    .flat();

  const uniqueColors: TotalColorItem[] = [];
  for (const prepackColor of prepackColors) {
    const color = uniqueColors.find((e) => e.hex === prepackColor.hex);
    if (color) {
      color.quantity += prepackColor.quantity;
    } else {
      uniqueColors.push(prepackColor);
    }
  }

  return uniqueColors;
}

function extractProductDataForBasketRow(
  product: dto.ReferencedProduct,
  products: dto.ProductsMap,
  settings: BasketMappingSettings,
) {
  const colorAttributeKeys = [
    'colortext',
    'colordescription',
    'colorfamily',
    'color',
  ];
  const colorCodeAttributeKeys = ['colorcode', 'colornumber', 'color'];
  return {
    productId: product.productId,
    displayedProductId: mapProductId(
      product.productId,
      settings?.additionalPrefix,
    ),
    displayedArticleId: mapProductId(
      product?.masterProductId ?? '',
      settings?.additionalPrefix,
    ),
    brandId: product.brand.brandId,
    title: getProductTitle(product, products, settings),
    color: getProductAttribute(product, colorAttributeKeys),
    colorText: getProductAttribute(product, colorAttributeKeys),
    colorCode: getProductAttribute(product, colorCodeAttributeKeys),
    mastercategoryname: getProductAttribute(product, ['mastercategoryname']),
    mastersubcategoryname: getProductAttribute(product, [
      'mastersubcategoryname',
    ]),
    brandName: getProductAttribute(product, ['brandname']),
    productlinename: getProductAttribute(product, ['productlinename']),
    divisionb2b: getProductAttribute(product, ['divisionb2b']),
    productclassb2b: getProductAttribute(product, ['productclassb2b']),
    isclosedoutproduct: getProductAttribute(product, ['isclosedoutproduct']),
  };
}

function getProductTitle(
  product: dto.ReferencedProduct,
  products: dto.ProductsMap,
  settings: BasketMappingSettings,
) {
  const productType = getProductType(product.productClassId);
  switch (productType) {
    case dto.ProductType.Material:
      return isNullish(settings.productTitleLevel)
        ? product.title
        : findProductByType(product, products, settings.productTitleLevel)
            ?.title ?? product.title;
    case dto.ProductType.Prepack:
      return (
        getProductTitleForPrepack(product, products, settings) ?? product.title
      );
    case dto.ProductType.Article:
    case dto.ProductType.Size:
    default:
      return product.title;
  }
}

function getProductTitleForPrepack(
  product: dto.ReferencedProduct,
  products: dto.ProductsMap,
  settings: BasketMappingSettings,
): string {
  if (
    isNullish(product.productCompositionPartReferences) ||
    (isNullish(settings.productTitleLevel) &&
      product.title !== product.productId)
  ) {
    return product.title;
  }

  const key = getProductKey(
    product.productCompositionPartReferences[0].product.brandId,
    product.productCompositionPartReferences[0].product.productId,
  );
  const compositeProduct = products[key];
  if (isNullish(compositeProduct)) {
    return product.title;
  }

  const masterProduct = findProduct(
    {
      brandId: compositeProduct.brand.brandId,
      productId: compositeProduct.masterProductId,
    },
    products,
  );

  return getProductTitle(masterProduct, products, settings);
}

export function getProductAttribute(
  product: dto.ReferencedProduct,
  keys: string[],
) {
  const attributes = keys
    .map((key) => product.attributes[key])
    .filter((attribute) => attribute && attribute.length)
    .flat();

  return (attributes.length ? attributes[0] : '') ?? '';
}

function getDeliveryKey(item: dto.Item): string {
  const isoDate = utcDateTime(
    item.expectedDeliveryDate ??
      item.displayDateFrom ??
      item.requestedDeliveryDate,
  ).toISODate();
  return `${item.deliveryId}_${isoDate}`;
}

function getDeliveryKeyForPrepack(item: dto.Item): string {
  const isoDate = utcDateTime(
    item.expectedDeliveryDate ??
      item.displayDateFrom ??
      item.requestedDeliveryDate,
  ).toISODate();
  return `${item.deliveryId}_${isoDate}_${item.productId}`;
}

function getDeliveryInfo(item: dto.Item, account: dto.BasketAccount) {
  const itemDeliveryId = item.deliveryId;
  const { consignmentStartDate, consignmentEndDate, requestedDeliveryDate } =
    account.deliveries.find(
      ({ deliveryId }) => deliveryId === itemDeliveryId,
    ) ?? {};

  return {
    consignmentStartDate,
    consignmentEndDate,
    requestedDeliveryDate,
  };
}

export function getMaterialWidthIds(
  items: dto.Item[],
  products: dto.ProductsMap,
): MaterialWidthIds[] {
  const widthIdAttribute = 'afs-material';
  const widthMap: Record<string, string> = {};
  Object.values(items).forEach((item) => {
    const product = findProduct(item, products);
    const width = getProductAttribute(product, ['width']);
    const id = getProductAttribute(product, [widthIdAttribute]);
    if (width && id) {
      widthMap[width] = id;
    }
  });

  return Object.entries(widthMap).map(([width, id]) => ({
    width,
    id,
  }));
}
